import React, { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Minus, Plus } from "lucide-react"
import { DateTime, Duration } from "luxon"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { numberFmt } from "@/util/numberFmt"

import { getFarmDriverTrips } from "../../api/getFarmDriverTrips"
import { getTripHours } from "../../util/getTripHours"
import { getTripsEfficiency } from "../../util/getTripsEfficiency"

import type { Farm } from "../../types/logisticsTypes"

const columnHelper = createColumnHelper<Farm>()

export default function DriverTripTable({ id, asOf }: { id: number; asOf: Date }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["driverTrips", id, asOf],
    queryFn: () => getFarmDriverTrips(id, asOf),
  })

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "expand",
        header: () => <>Show Trips</>,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            onClick={row.getToggleExpandedHandler()}
            disabled={!row.getCanExpand()}
          >
            {row.getIsExpanded() ? <Minus /> : <Plus />}
          </Button>
        ),
      }),
      columnHelper.accessor((f) => f.name, {
        id: "farm",
        header: () => <>Farm</>,
        cell: (context) => <>{context.getValue()}</>,
      }),
      columnHelper.accessor((f) => f.trips?.reduce((a, c) => a + (getTripHours(c) || 0), 0), {
        id: "hours",
        header: () => <>Hours Driven</>,
        cell: (context) => <>{numberFmt(context.getValue() || 0)}</>,
      }),
      columnHelper.accessor(() => 0, {
        id: "target",
        header: () => <>Target Hours</>,
        cell: (context) => <>{numberFmt(context.getValue() || 0)}</>,
      }),
      columnHelper.accessor((f) => getTripsEfficiency(f.trips || []), {
        id: "efficiency",
        header: () => <>Efficiency</>,
        cell: (context) => <>{numberFmt(context.getValue() || 0)}%</>,
      }),
      columnHelper.accessor((f) => f.trips?.reduce((a, c) => a + (c.unloadVolume || 0), 0), {
        id: "manure",
        header: () => <>Manure</>,
        cell: (context) => <>{numberFmt(context.getValue() || 0)}</>,
      }),
      columnHelper.accessor((f) => f.trips?.reduce((a, c) => a + (c.loadVolume || 0), 0), {
        id: "uf",
        header: () => <>UF</>,
        cell: (context) => <>{numberFmt(context.getValue() || 0)}</>,
      }),
    ],
    [],
  )

  const table = useReactTable({
    columns,
    data: data || [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) =>
      row.original.trips != undefined ? row.original.trips.length > 0 : false,
  })

  if (isError) {
    return <>Network Error</>
  }

  return (
    <Table className="table-fixed">
      {table.getHeaderGroups().map((hg) => (
        <TableHeader className="bg-accent sticky top-0 shadow-sm z-100" key={hg.id}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <TableHead className="p-4" key={i}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {isPending || !data
          ? [...Array(10).keys()].map((i) => (
              <TableRow key={i}>
                <TableCell colSpan={table.getAllColumns().length} className="h-[52.5px]">
                  <Skeleton className="w-full h-full" />
                </TableCell>
              </TableRow>
            ))
          : table.getRowModel().rows.map((row, i) => (
              <React.Fragment key={i}>
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell, i) => (
                    <TableCell key={i}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() &&
                  row.original.trips?.map((t) => {
                    const efficiency = 0
                    return (
                      <TableRow key={t.id} className="bg-muted">
                        <TableCell></TableCell>
                        <TableCell>
                          <div className="flex justify-between">
                            {t.farm || ""}
                            <div>
                              {t.start && DateTime.fromISO(t.start).toFormat("L/d h:mm a")}
                              <span className="px-2">-</span>
                              {t.end && DateTime.fromISO(t.end).toFormat("h:mm a ZZZZ")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {Duration.fromObject({
                            hours: getTripHours(t) || 0,
                          }).toFormat("h:mm")}
                        </TableCell>
                        <TableCell>
                          {getTripHours(t) === null
                            ? "N/A"
                            : Duration.fromObject({
                                minutes: 0,
                              }).toFormat("h:mm")}
                        </TableCell>
                        <TableCell
                          className={`${
                            efficiency != undefined &&
                            (efficiency >= 100 ? "text-status-good" : "text-status-bad")
                          }`}
                        >
                          {efficiency == undefined ? "N/A" : numberFmt(efficiency) + "%"}
                        </TableCell>
                        <TableCell>{numberFmt(t.unloadVolume || 0)}</TableCell>
                        <TableCell>{numberFmt(t.loadVolume || 0)}</TableCell>
                      </TableRow>
                    )
                  })}
              </React.Fragment>
            ))}
      </TableBody>
    </Table>
  )
}
